/**
 * TruDiagnosticOrderModal.tsx
 *
 * Thin LabOrderModalCore adapter for TruDiagnostic epigenetic / bio-age tests.
 * Option A pricing model: the test catalog is pulled LIVE from the TruDiagnostic
 * API (no per-practice product config). The API exposes no prices, so no price is
 * shown up front — Prospera's % markup (set in the Corp super-admin) is applied to
 * the order's actual cost at fulfillment. The selected SKU rides in metadata; the
 * order line item uses the integration's single default catalog product.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  createIntegrationOrder,
  getTruDiagnosticSkus,
  type EnabledIntegration,
  type CreateIntegrationOrderResult,
} from '@/services/integrations.service';
import { updatePatient } from '@/services/patients.service';
import { LabOrderModalCore, type LabOrderConfig, type LabOrderSubmitResult } from './LabOrderModalCore';

interface TruDiagnosticOrderModalProps {
  patientId: number;
  integration: EnabledIntegration;
  isOpen: boolean;
  onClose: () => void;
  onOrdered: (result: CreateIntegrationOrderResult, integrationProductId: number | null) => void;
}

export function TruDiagnosticOrderModal({ patientId, integration, isOpen, onClose, onOrdered }: TruDiagnosticOrderModalProps) {
  const [skuId, setSkuId] = useState('');

  const { data: skuData, isLoading: skusLoading } = useQuery({
    queryKey: ['trudiagnostic-skus'],
    queryFn: getTruDiagnosticSkus,
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });
  const skus = skuData?.skus ?? [];

  useEffect(() => {
    if (isOpen && skuId === '' && skus.length > 0) setSkuId(skus[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuData, isOpen]);
  useEffect(() => { if (!isOpen) setSkuId(''); }, [isOpen]);

  const selected = skus.find((s) => s.id === skuId) ?? null;
  // The order line item uses the integration's single default catalog product
  // (Option A has no per-SKU products); the real SKU travels in metadata.
  const lineProductId = integration.products?.[0]?.integrationProductId ?? null;

  const skuIdRef = useRef(skuId); skuIdRef.current = skuId;
  const selectedRef = useRef(selected); selectedRef.current = selected;
  const lineProductIdRef = useRef(lineProductId); lineProductIdRef.current = lineProductId;
  const onOrderedRef = useRef(onOrdered); onOrderedRef.current = onOrdered;

  const config = useMemo<LabOrderConfig>(() => ({
    vendor: 'trudiagnostic',
    title: 'Order TruDiagnostic Test',
    requireAddress: true,
    // Same as Axiom (also ship-to-patient, no lab-collection visit): identity
    // stays locked to the chart, and none of the lab-collection-visit UI
    // (notes, a specimen collection date, clinical vitals) applies to shipping
    // an at-home test kit — three fields that default to ON in LabOrderConfig
    // and have to be explicitly turned off, not just left unset.
    lockPatientIdentity: true,
    features: {
      presets: false, facilityProvider: false, billing: false, diagnoses: false, tests: false,
      notes: false, collectionDate: false, vitals: false,
    },
    submit: async (payload): Promise<LabOrderSubmitResult> => {
      const sk = selectedRef.current;
      if (!sk) throw new Error('Please select a test.');

      // A lab order may FILL a missing demographic, never change one — the
      // patient profile is the only place demographics are edited. The allowed
      // set and the was-it-blank test live in one place,
      // LabOrderModalCore.buildDemographicsFill(); name, e-mail and phone are
      // deliberately excluded.
      if (payload.demographicsFill) {
        await updatePatient(payload.patientId, payload.demographicsFill);
      }

      const pid = lineProductIdRef.current;
      const result = await createIntegrationOrder(payload.patientId, {
        key: 'trudiagnostic',
        items: pid != null ? [{ integrationProductId: pid, quantity: 1 }] : undefined,
        metadata: {
          skuId: sk.id,
          skuName: sk.name,
          shippingAddress: {
            recipientName: payload.recipientName || undefined,
            line1: payload.street,
            line2: payload.street2 || undefined,
            city: payload.city,
            state: payload.state,
            zip: payload.zip,
            country: payload.country || undefined,
            phone: payload.phone || undefined,
            patientShippingAddressId: payload.shippingAddressId ?? undefined,
          },
        },
      });
      onOrderedRef.current(result, pid);
      return { ok: true, lines: [`TruDiagnostic test ordered — Order #${result.integrationOrderId}`] };
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const topSlot = (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
      Pricing is set from TruDiagnostic at order time, with Prospera’s markup applied automatically.
    </div>
  );

  const itemsSlot = (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <span className="text-sm font-semibold text-slate-700">Test</span>
      </div>
      <div className="px-4 py-3">
        {skusLoading ? (
          <p className="text-xs text-slate-400 italic">Loading TruDiagnostic catalog…</p>
        ) : skus.length > 0 ? (
          <select
            value={skuId}
            onChange={(e) => setSkuId(e.target.value)}
            className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {skus.map((s) => (
              <option key={s.id} value={s.id}>{s.name}{s.description ? ` — ${s.description}` : ''}</option>
            ))}
          </select>
        ) : (
          <p className="text-xs text-amber-700">
            {skuData?.configured === false
              ? 'TruDiagnostic is not configured for this practice yet (missing API key).'
              : (skuData?.error || 'No tests available from TruDiagnostic right now.')}
          </p>
        )}
      </div>
    </div>
  );

  const confirmSlot = selected ? (
    <div className="space-y-1">
      <p className="font-semibold text-blue-900">Test:</p>
      <p className="text-blue-800">{selected.name}</p>
    </div>
  ) : null;

  return (
    <LabOrderModalCore
      config={config}
      patientId={patientId}
      open={isOpen}
      onClose={onClose}
      onOrdered={() => { /* handled by parent via onOrdered */ }}
      topSlot={topSlot}
      itemsSlot={itemsSlot}
      confirmSlot={confirmSlot}
    />
  );
}
